import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    age: "",
    legal_residence_country: "",
    guardian_email: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const isMinor =
    form.age && Number(form.age) >= 13 && Number(form.age) <= 17;

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register({
        name: form.name,
        email: form.email,
        password: form.password,
        age: form.age ? Number(form.age) : null,
        legal_residence_country: form.legal_residence_country || null,
        guardian_email: form.guardian_email || null,
      });
      const next = params.get("next");
      navigate(next || "/dashboard");
    } catch (err) {
      setError(err.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12 fade-in" data-testid="register-page">
      <div className="w-full max-w-md bg-white rounded-2xl border border-[color:var(--line)] p-8 shadow-sm">
        <h1 className="font-serif text-3xl mb-2 text-[color:var(--text-primary)]">Create your account</h1>
        <p className="text-sm text-[color:var(--text-muted)] mb-6">
          Start your first case in under a minute.
        </p>

        <button
          type="button"
          onClick={handleGoogle}
          data-testid="google-signup-btn"
          className="w-full mb-4 flex items-center justify-center gap-3 px-4 py-3 rounded-full border border-[color:var(--line)] bg-white hover:bg-[color:var(--surface-sage)] text-[color:var(--text-primary)] font-medium transition"
        >
          <svg className="w-5 h-5" viewBox="0 0 48 48" aria-hidden>
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.8 3l5.7-5.7C34 6.1 29.2 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.7 1.1 7.8 3l5.7-5.7C34 6.1 29.2 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"/>
            <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.4-4.5 2.4-7.2 2.4-5.2 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.7 16.2 44 24 44z"/>
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4.1 5.5l6.2 5.2C40.9 35.6 44 30.2 44 24c0-1.2-.1-2.3-.4-3.5z"/>
          </svg>
          Continue with Google
        </button>

        <div className="flex items-center gap-3 my-4 text-xs text-[color:var(--text-muted)]">
          <span className="h-px flex-1 bg-[color:var(--line)]" />
          or sign up with email
          <span className="h-px flex-1 bg-[color:var(--line)]" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm text-[color:var(--text-muted)] mb-1 block">Full name</label>
            <input
              type="text"
              required
              value={form.name}
              onChange={update("name")}
              className="w-full px-4 py-3 rounded-xl border border-[color:var(--line)] bg-white outline-none focus:ring-2 focus:ring-[color:var(--participant-a)]/30"
              data-testid="register-name"
            />
          </div>
          <div>
            <label className="text-sm text-[color:var(--text-muted)] mb-1 block">Email</label>
            <input
              type="email"
              required
              value={form.email}
              onChange={update("email")}
              className="w-full px-4 py-3 rounded-xl border border-[color:var(--line)] bg-white outline-none focus:ring-2 focus:ring-[color:var(--participant-a)]/30"
              data-testid="register-email"
            />
          </div>
          <div>
            <label className="text-sm text-[color:var(--text-muted)] mb-1 block">Password (min 6 chars)</label>
            <input
              type="password"
              required
              minLength={6}
              value={form.password}
              onChange={update("password")}
              className="w-full px-4 py-3 rounded-xl border border-[color:var(--line)] bg-white outline-none focus:ring-2 focus:ring-[color:var(--participant-a)]/30"
              data-testid="register-password"
            />
          </div>
          <div>
            <label className="text-sm text-[color:var(--text-muted)] mb-1 block">Age (optional)</label>
            <input
              type="number"
              min="0"
              max="120"
              value={form.age}
              onChange={update("age")}
              className="w-full px-4 py-3 rounded-xl border border-[color:var(--line)] bg-white outline-none focus:ring-2 focus:ring-[color:var(--participant-a)]/30"
              data-testid="register-age"
            />
            {isMinor && (
              <div
                className="mt-2 text-xs rounded-md bg-[color:var(--surface-sage)] border border-[color:var(--line)] p-3 text-[color:var(--text-primary)]"
                data-testid="minor-safety-notice"
              >
                <b>Safety notice for ages 13–17:</b> FairResolver is intended
                to help with respectful conversations. Please don't share
                personal information. If you feel unsafe, talk to a trusted
                adult.
                <div className="mt-3">
                  <label className="block text-[color:var(--text-muted)] mb-1">
                    Guardian email (optional now, required to upgrade or involve a professional mediator)
                  </label>
                  <input
                    type="email"
                    value={form.guardian_email}
                    onChange={update("guardian_email")}
                    placeholder="parent@example.com"
                    className="w-full px-3 py-2 rounded-lg border border-[color:var(--line)] bg-white outline-none focus:ring-2 focus:ring-[color:var(--participant-a)]/30 text-sm"
                    data-testid="register-guardian-email"
                  />
                  <p className="text-[10px] text-[color:var(--text-muted)] mt-1">
                    We'll send your guardian a consent link before any paid or
                    professional-mediator action. Free use of FairResolver
                    doesn't require consent.
                  </p>
                </div>
              </div>
            )}
          </div>
          <div>
            <label className="text-sm text-[color:var(--text-muted)] mb-1 block">Country of legal residence (optional)</label>
            <input
              type="text"
              value={form.legal_residence_country}
              onChange={update("legal_residence_country")}
              placeholder="e.g. South Africa"
              className="w-full px-4 py-3 rounded-xl border border-[color:var(--line)] bg-white outline-none focus:ring-2 focus:ring-[color:var(--participant-a)]/30"
              data-testid="register-country"
            />
            <p className="mt-1 text-xs text-[color:var(--text-muted)]">
              Used to match professional mediators registered with your national regulator.
            </p>
          </div>
          {error && (
            <div className="text-sm text-[color:var(--counter-error)]" data-testid="register-error">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full px-4 py-3 rounded-full bg-[color:var(--participant-a)] text-white font-semibold hover:opacity-90 transition disabled:opacity-50"
            data-testid="register-submit"
          >
            {loading ? "Creating..." : "Create account"}
          </button>
        </form>

        <p className="text-sm text-[color:var(--text-muted)] mt-6 text-center">
          Already have an account?{" "}
          <Link to="/login" className="font-medium text-[color:var(--participant-a)] hover:underline" data-testid="register-login-link">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
